import React, { useEffect } from 'react';
import { Box, CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import { WebPhoneProviderV3, useWebPhoneContextV3 } from '../../contexts/WebPhoneContextV3';
import { AuthProvider } from '../../contexts/AuthContext';
import WebPhoneStandaloneV3 from './WebPhoneStandaloneV3';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

// Inner component that has access to WebPhone context
const WebPhonePopupContent: React.FC = () => {
  const { callStatus } = useWebPhoneContextV3();

  useEffect(() => {
    // Set document title
    document.title = 'WebPhone V3';
  }, []);

  useEffect(() => {
    // Warn user on reload/close if there's an active call
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (callStatus === 'calling' || callStatus === 'ringing' || callStatus === 'in-call') {
        e.preventDefault();
        // Modern browsers show their own message, but we set returnValue anyway
        e.returnValue = 'Your call will be disconnected if you continue.';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [callStatus]);

  return (
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        bgcolor: 'background.default',
      }}
    >
      <WebPhoneStandaloneV3 />
    </Box>
  );
};

const WebPhonePopupV3: React.FC = () => {
  useEffect(() => {
    // Listen for auth tokens from parent window (for standalone WebPhone PWA)
    const handleMessage = (event: MessageEvent) => {
      // Verify origin for security
      const allowedOrigins = [
        'https://webphone.iatelecom.ca',
        'https://webphone.ourbizphone.com',
        'http://localhost:3000'
      ];

      if (!allowedOrigins.includes(event.origin)) {
        console.log('[WebPhonePopupV3] Ignoring message from unauthorized origin:', event.origin);
        return;
      }

      if (event.data && event.data.type === 'AUTH_TOKENS') {
        console.log('[WebPhonePopupV3] Received auth tokens from parent window');

        // Store tokens in both localStorage and sessionStorage for compatibility
        if (event.data.accessToken) {
          localStorage.setItem('access_token', event.data.accessToken);
          sessionStorage.setItem('access_token', event.data.accessToken);
        }

        if (event.data.refreshToken) {
          localStorage.setItem('refresh_token', event.data.refreshToken);
          sessionStorage.setItem('refresh_token', event.data.refreshToken);
        }

        if (event.data.memberData) {
          localStorage.setItem('member_data', event.data.memberData);
          sessionStorage.setItem('member_data', event.data.memberData);
        }

        console.log('[WebPhonePopupV3] Tokens stored, reloading page...');
        // Reload to apply the new auth state
        window.location.reload();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <WebPhoneProviderV3>
          <WebPhonePopupContent />
        </WebPhoneProviderV3>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default WebPhonePopupV3;
